import { Layout, defaultTheme, Menu } from "react-admin";
import { createTheme } from "@mui/material";
import { yellow, orange, red } from "@mui/material/colors";
import { alpha } from "@mui/material/styles";
import { ResourceMenuItems } from "../utils/resourceMenuItems";

const PRIMARY = "#2563eb";
const BORDER = "#e2e8f0";

const RaLabeled = {
  styleOverrides: {
    root: {
      marginBottom: ".8em",
      "& .RaLabeled-label": {
        color: PRIMARY,
        fontWeight: 600,
        fontSize: "0.75rem",
        textTransform: "uppercase",
        letterSpacing: "0.05em",
      },
      "& .MuiTypography-body2": {
        fontSize: "1em",
      },
    },
  },
};

export const jiTheme = createTheme({
  ...defaultTheme,
  palette: {
    primary: {
      main: PRIMARY,
    },
    secondary: {
      main: "#6366f1",
    },
    background: {
      paper: "#ffffff",
      default: "#f1f5f9",
    },
    specialColors: {
      testing: alpha(orange[600], 0.3),
      oos: alpha(red[600], 0.3),
      highlight: alpha(yellow[100], 0.7),
    },
  },
  shape: {
    borderRadius: 8,
  },
  typography: {
    fontFamily: '"Inter", "Roboto", "Helvetica", "Arial", sans-serif',
    h6: { fontWeight: 600 },
    subtitle1: { fontWeight: 500 },
    button: { fontWeight: 600, letterSpacing: 0, textTransform: "none" },
  },
  components: {
    ...defaultTheme.components,
    RaLabeled,
    MuiAppBar: {
      styleOverrides: {
        root: {
          backgroundColor: "#ffffff",
          color: "#1e293b",
          boxShadow: `0 1px 0 0 ${BORDER}`,
          backgroundImage: "none",
        },
      },
    },
    MuiCard: {
      styleOverrides: {
        root: {
          boxShadow:
            "0 1px 3px 0 rgb(0 0 0 / 0.08), 0 1px 2px -1px rgb(0 0 0 / 0.06)",
          border: `1px solid ${BORDER}`,
          backgroundImage: "none",
        },
      },
    },
    MuiPaper: {
      styleOverrides: {
        root: {
          backgroundImage: "none",
        },
        elevation1: {
          boxShadow:
            "0 1px 3px 0 rgb(0 0 0 / 0.08), 0 1px 2px -1px rgb(0 0 0 / 0.06)",
        },
      },
    },
    MuiButton: {
      styleOverrides: {
        root: {
          textTransform: "none",
          fontWeight: 600,
          letterSpacing: 0,
        },
        contained: {
          boxShadow: "none",
          "&:hover": {
            boxShadow: "0 2px 6px 0 rgb(0 0 0 / 0.15)",
          },
        },
      },
    },
    MuiTableCell: {
      styleOverrides: {
        head: {
          fontWeight: 700,
          backgroundColor: "#f8fafc",
          color: "#64748b",
          fontSize: "0.7rem",
          textTransform: "uppercase",
          letterSpacing: "0.06em",
          borderBottom: `2px solid ${BORDER}`,
        },
        body: {
          borderBottom: `1px solid ${BORDER}`,
          color: "#1e293b",
        },
      },
    },
    MuiTableRow: {
      styleOverrides: {
        root: {
          "&:hover td": {
            backgroundColor: "#f8fafc",
          },
          "&:last-child td": {
            borderBottom: 0,
          },
        },
      },
    },
    MuiChip: {
      styleOverrides: {
        root: {
          fontWeight: 500,
          fontSize: "0.75rem",
        },
      },
    },
    MuiInputBase: {
      styleOverrides: {
        root: {
          backgroundColor: "#ffffff",
          fontSize: "0.9rem",
        },
      },
    },
    MuiOutlinedInput: {
      styleOverrides: {
        root: {
          "& .MuiOutlinedInput-notchedOutline": {
            borderColor: BORDER,
          },
          "&:hover .MuiOutlinedInput-notchedOutline": {
            borderColor: "#94a3b8",
          },
        },
      },
    },
    MuiMenuItem: {
      styleOverrides: {
        root: {
          fontSize: "0.875rem",
        },
      },
    },
    RaMenuItemLink: {
      styleOverrides: {
        root: {
          borderRadius: 6,
          margin: "2px 8px",
          padding: "8px 12px",
          fontSize: "0.875rem",
          fontWeight: 500,
        },
      },
    },
    RaList: {
      styleOverrides: {
        root: {
          "& .RaList-main": {
            borderRadius: 8,
          },
        },
      },
    },
    RaDatagrid: {
      styleOverrides: {
        root: {
          "& .RaDatagrid-headerCell": {
            backgroundColor: "#f8fafc",
          },
          "& .RaDatagrid-rowCell": {
            fontSize: "0.875rem",
          },
        },
      },
    },
    RaTopToolbar: {
      styleOverrides: {
        root: {
          paddingTop: 8,
          paddingBottom: 8,
          gap: 8,
          alignItems: "center",
          "& .MuiButton-root": {
            borderRadius: 8,
          },
        },
      },
    },
    RaFilterForm: {
      styleOverrides: {
        root: {
          gap: 8,
          padding: "8px 0",
          "& .MuiFormControl-root": {
            marginTop: 0,
            marginBottom: 0,
          },
        },
      },
    },
    RaFilterButton: {
      styleOverrides: {
        root: {
          "& .MuiButton-root": {
            borderRadius: 8,
            border: `1px solid ${BORDER}`,
            color: "#475569",
            backgroundColor: "#ffffff",
            "&:hover": {
              backgroundColor: "#f8fafc",
            },
          },
        },
      },
    },
    RaBulkActionsToolbar: {
      styleOverrides: {
        root: {
          backgroundColor: "#eff6ff",
          borderRadius: 8,
          "& .MuiTypography-root": {
            color: PRIMARY,
            fontWeight: 600,
          },
        },
      },
    },
    RaCreate: {
      styleOverrides: {
        root: {
          "& .RaCreate-main": {
            maxWidth: 720,
          },
        },
      },
    },
    RaEdit: {
      styleOverrides: {
        root: {
          "& .RaEdit-main": {
            maxWidth: 720,
          },
        },
      },
    },
    MuiLinearProgress: {
      styleOverrides: {
        root: {
          height: 3,
          borderRadius: 0,
          backgroundColor: "#dbeafe",
        },
        bar: {
          backgroundColor: PRIMARY,
        },
      },
    },
    MuiTooltip: {
      styleOverrides: {
        tooltip: {
          backgroundColor: "#1e293b",
          fontSize: "0.75rem",
          fontWeight: 500,
          borderRadius: 6,
          padding: "5px 10px",
        },
        arrow: {
          color: "#1e293b",
        },
      },
    },
    MuiDivider: {
      styleOverrides: {
        root: {
          borderColor: BORDER,
        },
      },
    },
    MuiFormLabel: {
      styleOverrides: {
        root: {
          fontSize: "0.875rem",
          fontWeight: 500,
          color: "#475569",
        },
      },
    },
    MuiInputLabel: {
      styleOverrides: {
        root: {
          fontSize: "0.875rem",
          fontWeight: 500,
        },
      },
    },
    MuiCssBaseline: {
      styleOverrides: {
        body: {
          fontFamily: '"Inter", "Roboto", "Helvetica", "Arial", sans-serif',
          scrollbarWidth: "thin",
          scrollbarColor: "#cbd5e1 transparent",
          "&::-webkit-scrollbar": {
            width: 6,
            height: 6,
          },
          "&::-webkit-scrollbar-track": {
            background: "transparent",
          },
          "&::-webkit-scrollbar-thumb": {
            background: "#cbd5e1",
            borderRadius: 999,
          },
          "&::-webkit-scrollbar-thumb:hover": {
            background: "#94a3b8",
          },
        },
      },
    },
  },
});

const MyMenu = () => (
  <Menu>
    <Menu.DashboardItem />
    <ResourceMenuItems />
  </Menu>
);

const MyLayout = (props) => <Layout {...props} menu={MyMenu} />;

export default MyLayout;
